import React, { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faLink,
  faPlus,
  faSatelliteDish,
  faShareNodes,
  faSpinner,
  faTrash,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'

import { useDocumentViewer } from '../../../contexts/DocumentViewerContext'
import { profileService } from '../../../services/profileService'

import type {
  ControlCenterAsset,
  ControlCenterJob,
  ControlCenterMunicipality,
  ControlCenterPerson,
} from '../../../types/controlCenter'
import {
  EXTERNAL_MUNICIPALITY_COLOR,
  licenseTypeLabel,
} from '../../../utils/controlCenterMunicipalityLicenses'
import {
  getEntityLastGpsTimestampMs,
  type FleetIssue,
} from '../../../utils/tallyFleetEntities'

import {
  createEmptyOperationStep,
  operatorLabel,
  type OperationInputs,
  type OperationMapPoint,
  type OperationResult,
  type OperationStep,
  type OperatorDef,
  type OperatorId,
} from '../operations'

import type { EntityCategory } from './controlCenterMapTypes'
import {
  ASSET_KIND_COLOR,
  MUNICIPALITY_LEVEL_COLOR,
  PERSON_KIND_COLOR,
  assetKindLabel,
  copyTextToClipboard,
  formatClockedSince,
  formatGpsMs,
  formatRoundedMiles,
  jobStatusColor,
  personHasMapPin,
  personKindLabel,
  statusTone,
  titleCase,
} from './mapEntityPresentation'
import { dragPayloadForResultPoint, useDomEntityDrag } from './useEntityDrag'
import {
  PanelOperatorSection,
  type OperatorPopupDestinationOption,
} from './OperatorPopupControls'

export type OutputPanelInlineOperations = {
  targetId: string
  variant: 'entity' | 'result'
  operators: OperatorDef[]
  destinationOptions: OperatorPopupDestinationOption[]
  preferredDestinationRef: string
  operationResultOperatorId: OperatorId | null
  openFocusToken: number
  operationRunning: boolean
  onRun: (op: OperatorDef, inputs: OperationInputs) => void
}

export type OutputPanelPlaceFocus = {
  point: OperationMapPoint
  operations: OutputPanelInlineOperations
}

export type OutputPanelMyLocationPlace = {
  point: OperationMapPoint
  focused: boolean
  operations: OutputPanelInlineOperations | null
}

/** Output-panel map/prior result row — click to focus, drag into destination fields. */
export function OutputResultPointRow({
  point,
  fromStepId,
  selected,
  onSelect,
  mapRef,
}: {
  point: OperationMapPoint
  fromStepId: string
  selected: boolean
  onSelect: () => void
  mapRef?: React.RefObject<L.Map | null>
}) {
  const dragPayload = useMemo(
    () => dragPayloadForResultPoint(point, fromStepId),
    [point, fromStepId],
  )
  const { onPointerDown, suppressClickRef } = useDomEntityDrag(dragPayload, mapRef)

  return (
    <li>
      <button
        type="button"
        className={`flex w-full cursor-grab touch-none items-start gap-2 rounded-md border px-1.5 py-1 text-left text-[11px] transition-colors active:cursor-grabbing ${
          selected
            ? 'border-primary bg-primary/10'
            : 'border-transparent hover:border-base-300 hover:bg-base-200/60'
        }`}
        aria-pressed={selected}
        title={`${point.name} · drag into a destination field`}
        onPointerDown={onPointerDown}
        onClick={() => {
          if (suppressClickRef.current) return
          onSelect()
        }}
      >
        <span
          className="mt-1 h-2 w-2 shrink-0 rounded-sm"
          style={{ backgroundColor: point.color }}
          aria-hidden
        />
        <span className="min-w-0">
          <span className="block font-medium">{point.name}</span>
          {point.subtitle ? (
            <span className="text-base-content/55">{point.subtitle}</span>
          ) : null}
        </span>
      </button>
    </li>
  )
}

export function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string
  value: React.ReactNode
  valueClassName?: string
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className="grid grid-cols-[minmax(0,0.95fr)_minmax(0,1.25fr)] gap-x-2 gap-y-0.5 border-b border-base-200 py-2 last:border-0 text-xs">
      <dt className="text-base-content/60 font-medium">{label}</dt>
      <dd className={`break-words ${valueClassName || ''}`.trim()}>{value}</dd>
    </div>
  )
}

export function operationStepLabel(step: OperationStep, index: number): string {
  const name = operatorLabel(step.operatorId) || step.result?.title || 'New step'
  return `${index + 1} ${name}`
}

export const MAP_RESULTS_PREVIEW_COUNT = 5

function EntitySectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
      {children}
    </div>
  )
}

/** Entity card: collapsed header until focused; then overview → operations → more details. */
export function EntityPanelCard({
  expanded,
  onFocus,
  onClear,
  clearLabel,
  overview,
  overviewDetails,
  operations,
  detailsExpanded,
  onToggleDetails,
  details,
  className,
}: {
  /** When true, show overview/ops/details body (entity is focused). */
  expanded: boolean
  onFocus: () => void
  onClear: () => void
  clearLabel: string
  overview: React.ReactNode
  overviewDetails?: React.ReactNode
  operations?: React.ReactNode
  detailsExpanded: boolean
  onToggleDetails: () => void
  details?: React.ReactNode
  className?: string
}) {
  const hasDetails = Boolean(details)
  return (
    <section
      className={`mb-3 rounded-lg border last:mb-0 ${
        className ?? (expanded ? 'border-primary/35 bg-primary/5' : 'border-base-200')
      }`}
    >
      <div className="flex items-start gap-1 px-2.5 py-2">
        <button
          type="button"
          className="min-w-0 flex-1 rounded-md px-0.5 py-0.5 text-left hover:bg-base-200/60"
          aria-expanded={expanded}
          onClick={onFocus}
        >
          {overview}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs shrink-0"
          aria-label={clearLabel}
          onClick={(e) => {
            e.stopPropagation()
            onClear()
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {expanded ? (
        <>
          {overviewDetails ? (
            <div className="border-t border-base-200 px-2.5 py-2">
              <EntitySectionLabel>Overview</EntitySectionLabel>
              {overviewDetails}
            </div>
          ) : null}

          {operations ? (
            <div className="border-t border-base-200 px-2.5 py-2">{operations}</div>
          ) : null}

          {hasDetails ? (
            <div className="border-t border-base-200">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left hover:bg-base-200/60"
                aria-expanded={detailsExpanded}
                onClick={onToggleDetails}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                  More details
                </span>
                <FontAwesomeIcon
                  icon={detailsExpanded ? faChevronUp : faChevronDown}
                  className="text-[10px] opacity-60"
                />
              </button>
              {detailsExpanded ? <div className="px-2.5 pb-2">{details}</div> : null}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

export function CollapsibleEntityCard({
  entityId,
  expanded,
  onToggle,
  onClear,
  clearLabel,
  header,
  children,
  className,
}: {
  entityId: string
  expanded: boolean
  onToggle: (entityId: string) => void
  onClear: () => void
  clearLabel: string
  header: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section
      className={`mb-3 rounded-lg border last:mb-0 ${
        className ?? 'border-base-200'
      }`}
    >
      <div className="flex items-start gap-1 px-2.5 py-2">
        <button
          type="button"
          className="min-w-0 flex-1 rounded-md px-0.5 py-0.5 text-left hover:bg-base-200/60"
          aria-expanded={expanded}
          onClick={() => onToggle(entityId)}
        >
          {header}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square shrink-0"
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          onClick={() => onToggle(entityId)}
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="text-[10px]"
          />
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs shrink-0"
          aria-label={clearLabel}
          onClick={onClear}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {expanded ? <div className="border-t border-base-200 px-2.5 py-2">{children}</div> : null}
    </section>
  )
}

export function OutputPanel({
  pinnedJobs,
  pinnedPeople,
  pinnedAssets,
  pinnedMunicipalities,
  focusId,
  boundaryLoading,
  hasBoundary,
  operationResult,
  focusedResultPointId,
  onFocusResultPoint,
  fleetGroupLabels,
  assetIssuesById,
  focusedAssetImages,
  assetImagesLoading,
  assetGalleryIdx,
  onAssetGalleryIdxChange,
  onFocusPinnedEntity,
  onRemovePinnedEntity,
  onClearOperation,
  steps,
  activeStepId,
  onSelectStep,
  onAddStep,
  onRemoveStep,
  onPickPriorPoint,
  onShare,
  canShare,
  shareCopied,
  mapRef,
  onSelectTableEntity,
  inlineEntityOperations = null,
  myLocationPlace = null,
  onClearMyLocation,
  focusedPlace = null,
  onClearFocusedPlace,
}: {
  pinnedJobs: ControlCenterJob[]
  pinnedPeople: ControlCenterPerson[]
  pinnedAssets: ControlCenterAsset[]
  pinnedMunicipalities: ControlCenterMunicipality[]
  focusId: string | null
  boundaryLoading: boolean
  hasBoundary: boolean
  operationResult: OperationResult | null
  focusedResultPointId: string | null
  onFocusResultPoint: (pointId: string) => void
  fleetGroupLabels: Map<number, string>
  assetIssuesById: Map<string, FleetIssue[]>
  focusedAssetImages: string[]
  assetImagesLoading: boolean
  assetGalleryIdx: number
  onAssetGalleryIdxChange: (idx: number | ((prev: number) => number)) => void
  onFocusPinnedEntity: (id: string, category: EntityCategory) => void
  onRemovePinnedEntity: (id: string) => void
  onClearOperation: () => void
  steps: OperationStep[]
  activeStepId: string
  onSelectStep: (stepId: string) => void
  onAddStep: () => void
  onRemoveStep: (stepId: string) => void
  onPickPriorPoint: (fromStepId: string, pointId: string) => void
  onShare: () => void
  canShare: boolean
  shareCopied: boolean
  mapRef?: React.RefObject<L.Map | null>
  /** Pin + focus a job/person from a Time breakdown row. */
  onSelectTableEntity?: (category: 'job' | 'person', id: string) => void
  /** Mobile: ops UI embedded in the focused entity card. */
  inlineEntityOperations?: OutputPanelInlineOperations | null
  /** Persists while GPS pin is active — collapsed until focused. */
  myLocationPlace?: OutputPanelMyLocationPlace | null
  onClearMyLocation?: () => void
  /** Mobile: focused place/result pin (no portal entity). */
  focusedPlace?: OutputPanelPlaceFocus | null
  onClearFocusedPlace?: () => void
}) {
  const pinnedCount =
    pinnedJobs.length +
    pinnedPeople.length +
    pinnedAssets.length +
    pinnedMunicipalities.length +
    (myLocationPlace ? 1 : 0)
  const hasSelection = pinnedCount > 0 || Boolean(focusedPlace)

  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === activeStepId),
  )
  const activeStep = steps[activeIndex] ?? steps[0]
  const activeStepTitle = operationStepLabel(activeStep ?? createEmptyOperationStep(), activeIndex)
  const priorStepsWithResults = useMemo(
    () =>
      steps
        .slice(0, activeIndex)
        .map((step, idx) => ({ step, idx }))
        .filter(({ step }) => (step.result?.mapPoints.length ?? 0) > 0),
    [steps, activeIndex],
  )
  const isEmptyChainStep = Boolean(activeStep && !activeStep.result)
  const [mapsLinkCopied, setMapsLinkCopied] = useState(false)
  const mapsLinkCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [mapResultsExpanded, setMapResultsExpanded] = useState(false)
  const [priorResultsExpandedByStep, setPriorResultsExpandedByStep] = useState<
    Record<string, boolean>
  >({})
  const [expandedEntityId, setExpandedEntityId] = useState<string | null>(null)
  const [operationExpanded, setOperationExpanded] = useState(true)
  const [openingCertKey, setOpeningCertKey] = useState<string | null>(null)
  const { openDocument } = useDocumentViewer()
  const panelScrollRef = useRef<HTMLDivElement | null>(null)
  const etaMapsUrl =
    operationResult?.operatorId === 'eta' ? operationResult.mapsDirectionsUrl : undefined

  useEffect(() => {
    setMapsLinkCopied(false)
    if (mapsLinkCopiedTimeoutRef.current) {
      clearTimeout(mapsLinkCopiedTimeoutRef.current)
      mapsLinkCopiedTimeoutRef.current = null
    }
  }, [etaMapsUrl, operationResult?.ranAt])

  const openCertFile = async (row: Record<string, string>) => {
    const employeeId = String(row.portalEmployeeId || '').trim()
    const certType = String(row.certType || '').trim()
    if (!employeeId || !certType || row.hasFile !== '1') return
    const title = row.name || 'Certification'
    const key = `${employeeId}:${certType}`
    setOpeningCertKey(key)
    openDocument(title, 'PDF')
    try {
      const blobUrl = await profileService.getCertFileBlobUrl(employeeId, certType)
      openDocument(title, 'PDF', blobUrl)
    } catch {
      openDocument(title, 'PDF', null, 'Failed to load certificate document.')
    } finally {
      setOpeningCertKey((current) => (current === key ? null : current))
    }
  }

  useEffect(() => {
    return () => {
      if (mapsLinkCopiedTimeoutRef.current) clearTimeout(mapsLinkCopiedTimeoutRef.current)
    }
  }, [])

  // Selecting an entity opens its card, closes other entities, and collapses the operation.
  useEffect(() => {
    if (!focusId) return
    // Overview + ops stay visible; details start collapsed to save space.
    setExpandedEntityId(null)
    setOperationExpanded(false)
  }, [focusId])

  useEffect(() => {
    if (!myLocationPlace?.focused) return
    setExpandedEntityId(null)
    setOperationExpanded(false)
  }, [myLocationPlace?.focused, myLocationPlace?.point.id])

  useEffect(() => {
    if (!focusedPlace) return
    setExpandedEntityId(null)
    setOperationExpanded(false)
  }, [focusedPlace?.point.id])

  // Running an operation expands the result card, collapses entity details, and scrolls to top.
  useEffect(() => {
    if (!operationResult?.ranAt) return
    setExpandedEntityId(null)
    setMapResultsExpanded(false)
    setOperationExpanded(true)
    const el = panelScrollRef.current
    if (!el) return
    el.scrollTo({ top: 0, behavior: 'smooth' })
  }, [operationResult?.ranAt])

  // Collapsing the operation card does not auto-expand entity details.
  useEffect(() => {
    if (operationExpanded) return
    setExpandedEntityId(null)
  }, [operationExpanded])

  useEffect(() => {
    if (!focusedResultPointId || !operationResult) return
    const idx = operationResult.mapPoints.findIndex((p) => p.id === focusedResultPointId)
    if (idx >= MAP_RESULTS_PREVIEW_COUNT) {
      setMapResultsExpanded(true)
      setOperationExpanded(true)
    }
  }, [focusedResultPointId, operationResult])

  // Expand a prior-results step list when the selected point sits past the preview.
  useEffect(() => {
    const source = activeStep?.inputSource
    if (source?.kind !== 'priorPoint') return
    const from = steps.find((s) => s.id === source.fromStepId)
    const idx = from?.result?.mapPoints.findIndex((p) => p.id === source.pointId) ?? -1
    if (idx < MAP_RESULTS_PREVIEW_COUNT) return
    setPriorResultsExpandedByStep((prev) =>
      prev[source.fromStepId] ? prev : { ...prev, [source.fromStepId]: true },
    )
  }, [activeStep?.inputSource, steps])

  useEffect(() => {
    setPriorResultsExpandedByStep({})
  }, [activeStepId])

  const toggleEntityExpanded = (entityId: string) => {
    setExpandedEntityId((prev) => (prev === entityId ? null : entityId))
  }

  const toggleOperationExpanded = (_entityId: string) => {
    setOperationExpanded((prev) => {
      const next = !prev
      if (next) setExpandedEntityId(null)
      return next
    })
  }

  const copyEtaMapsLink = async () => {
    if (!etaMapsUrl) return
    const ok = await copyTextToClipboard(etaMapsUrl)
    if (!ok) return
    setMapsLinkCopied(true)
    if (mapsLinkCopiedTimeoutRef.current) clearTimeout(mapsLinkCopiedTimeoutRef.current)
    mapsLinkCopiedTimeoutRef.current = setTimeout(() => setMapsLinkCopied(false), 2000)
  }

  // Rows that list the same entities already shown as map points are redundant.
  const operationDisplayRows = useMemo(() => {
    if (!operationResult) return []
    if (operationResult.mapPoints.length === 0) return operationResult.rows
    const mapNames = new Set(
      operationResult.mapPoints
        .map((p) => p.name.trim().toLowerCase())
        .filter(Boolean),
    )
    return operationResult.rows.filter((row) => {
      const label = row.label.trim().toLowerCase()
      const valuePrimary = row.value.split('·')[0].trim().toLowerCase()
      if (mapNames.has(label) || mapNames.has(valuePrimary)) return false
      return true
    })
  }, [operationResult])

  const visibleMapPoints = useMemo(() => {
    if (!operationResult) return []
    if (mapResultsExpanded) return operationResult.mapPoints
    return operationResult.mapPoints.slice(0, MAP_RESULTS_PREVIEW_COUNT)
  }, [operationResult, mapResultsExpanded])

  const hiddenMapResultCount = operationResult
    ? Math.max(0, operationResult.mapPoints.length - MAP_RESULTS_PREVIEW_COUNT)
    : 0

  return (
    <div className="pointer-events-auto flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl border-2 border-base-300 bg-base-100 shadow-lg">
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-base-200 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="dropdown">
            <button
              type="button"
              tabIndex={0}
              className="btn btn-ghost btn-xs h-auto min-h-0 gap-1.5 px-1 py-0.5 normal-case"
              aria-label="Select operation step"
              aria-haspopup="listbox"
            >
              <h3 className="text-sm font-semibold text-base-content">{activeStepTitle}</h3>
              <FontAwesomeIcon icon={faChevronDown} className="text-[10px] opacity-60" />
            </button>
            <ul
              tabIndex={0}
              className="dropdown-content menu z-30 mt-1 w-56 rounded-xl border-2 border-base-300 bg-base-100 p-1 shadow-lg"
              role="listbox"
            >
              {steps.map((step, idx) => {
                const selected = step.id === activeStepId
                const label = operationStepLabel(step, idx)
                return (
                  <li key={step.id} role="option" aria-selected={selected}>
                    <div
                      className={`flex items-center gap-1 rounded-lg px-1 py-0.5 ${
                        selected ? 'bg-primary/10' : ''
                      }`}
                    >
                      <button
                        type="button"
                        className={`min-w-0 flex-1 truncate px-1.5 py-1 text-left text-sm ${
                          selected ? 'font-semibold' : ''
                        }`}
                        onClick={() => {
                          onSelectStep(step.id)
                          // DaisyUI dropdown stays open on :focus — blur to close after pick.
                          ;(document.activeElement as HTMLElement | null)?.blur()
                        }}
                      >
                        {label}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-square shrink-0"
                        aria-label={`Delete ${label}`}
                        disabled={steps.length <= 1}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onRemoveStep(step.id)
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
          {operationResult ? (
            <p className="mt-0.5 text-[11px] text-base-content/50">
              {operationResult.title} · {operationResult.entityName}
            </p>
          ) : hasSelection ? (
            <p className="mt-0.5 text-[11px] text-base-content/50">
              {pinnedCount} pinned
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className={`btn btn-square btn-sm ${
              shareCopied ? 'btn-success' : 'btn-ghost'
            }`}
            disabled={!canShare}
            onClick={onShare}
            aria-label={
              shareCopied
                ? 'Results copied to clipboard'
                : 'Share results through this step'
            }
            title={
              shareCopied
                ? 'Copied!'
                : 'Copy plain-English results through this step'
            }
          >
            <FontAwesomeIcon
              icon={shareCopied ? faCheck : faShareNodes}
              className="text-xs"
            />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-square btn-sm"
            aria-label="Add operation step"
            onClick={onAddStep}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      <div
        ref={panelScrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 [scrollbar-gutter:stable]"
      >
        {operationResult ? (
          <CollapsibleEntityCard
            entityId="operation-result"
            expanded={operationExpanded}
            onToggle={toggleOperationExpanded}
            onClear={onClearOperation}
            clearLabel="Clear operation result"
            className="border-primary/25 bg-primary/5"
            header={
              <div className="min-w-0">
                <div className="text-xs font-semibold text-primary">{operationResult.title}</div>
                <p className="mt-0.5 text-[11px] text-base-content/70">{operationResult.summary}</p>
              </div>
            }
          >
            <dl>
              {operationDisplayRows.map((row) => (
                <DetailRow key={`${row.label}-${row.value}`} label={row.label} value={row.value} />
              ))}
            </dl>
            {operationResult.table ? (
              <div className="mt-2 overflow-x-auto border-t border-base-200 pt-2">
                <table className="table table-xs w-max min-w-full">
                  <thead>
                    <tr>
                      {operationResult.table.columns.map((col) => (
                        <th
                          key={col.key}
                          className={
                            col.align === 'right'
                              ? 'whitespace-nowrap text-right normal-case'
                              : 'whitespace-nowrap normal-case'
                          }
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {operationResult.table.rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={operationResult.table.columns.length}
                          className="text-base-content/50"
                        >
                          {operationResult.table.emptyMessage || 'No entries in range'}
                        </td>
                      </tr>
                    ) : (
                      operationResult.table.rows.map((row, idx) => {
                        const entityCategory = operationResult.table!.rowEntityCategory
                        const entityId = String(row.id || '').trim()
                        const selectable =
                          Boolean(onSelectTableEntity) &&
                          Boolean(entityCategory) &&
                          Boolean(entityId) &&
                          entityId !== '__none__'
                        const focused = selectable && focusId === entityId
                        return (
                          <tr
                            key={`${row.name ?? 'row'}-${entityId || idx}`}
                            className={
                              selectable
                                ? `cursor-pointer transition-colors hover:bg-primary/10 ${
                                    focused ? 'bg-primary/15' : ''
                                  }`
                                : undefined
                            }
                            title={selectable ? `Select ${row.name}` : undefined}
                            onClick={
                              selectable
                                ? () => onSelectTableEntity?.(entityCategory!, entityId)
                                : undefined
                            }
                          >
                            {operationResult.table!.columns.map((col) => (
                              <td
                                key={col.key}
                                className={
                                  col.align === 'right'
                                    ? 'whitespace-nowrap text-right font-mono'
                                    : 'whitespace-nowrap'
                                }
                              >
                                {col.key === 'file' && row.hasFile === '1' ? (
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-xs"
                                    disabled={
                                      openingCertKey === `${row.portalEmployeeId}:${row.certType}`
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      void openCertFile(row)
                                    }}
                                  >
                                    {openingCertKey === `${row.portalEmployeeId}:${row.certType}` ? (
                                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    ) : (
                                      'View PDF'
                                    )}
                                  </button>
                                ) : (
                                  row[col.key] || '—'
                                )}
                              </td>
                            ))}
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                  {operationResult.table.footer ? (
                    <tfoot>
                      <tr className="font-semibold">
                        {operationResult.table.columns.map((col) => (
                          <td
                            key={col.key}
                            className={
                              col.align === 'right'
                                ? 'whitespace-nowrap text-right font-mono'
                                : 'whitespace-nowrap'
                            }
                          >
                            {operationResult.table!.footer![col.key] ?? ''}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            ) : null}
            {etaMapsUrl ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  className={`btn btn-xs gap-1.5 ${
                    mapsLinkCopied ? 'btn-success' : 'btn-ghost border border-base-300'
                  }`}
                  aria-label={
                    mapsLinkCopied
                      ? 'Google Maps directions link copied'
                      : 'Copy Google Maps directions link'
                  }
                  title={
                    mapsLinkCopied
                      ? 'Copied!'
                      : 'Copy Google Maps directions link'
                  }
                  onClick={() => {
                    void copyEtaMapsLink()
                  }}
                >
                  <FontAwesomeIcon
                    icon={mapsLinkCopied ? faCheck : faLink}
                    className="text-[10px]"
                  />
                  {mapsLinkCopied ? 'Copied' : 'Copy Maps directions'}
                </button>
                <a
                  href={etaMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-xs btn-ghost gap-1.5 border border-base-300"
                  aria-label="Open Google Maps directions in a new tab"
                  title="Open Google Maps directions in a new tab"
                >
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                  Open directions
                </a>
              </div>
            ) : null}
            {operationResult.mapPoints.length > 0 ? (
              <div className="mt-2 border-t border-base-200 pt-2">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                  Map results ({operationResult.mapPoints.length})
                </div>
                <ul className="space-y-1">
                  {visibleMapPoints.map((point) => (
                    <OutputResultPointRow
                      key={point.id}
                      point={point}
                      fromStepId={activeStepId}
                      selected={focusedResultPointId === point.id}
                      onSelect={() => onFocusResultPoint(point.id)}
                      mapRef={mapRef}
                    />
                  ))}
                </ul>
                {hiddenMapResultCount > 0 && !mapResultsExpanded ? (
                  <button
                    type="button"
                    className="badge badge-sm badge-ghost mt-2 cursor-pointer border border-base-300"
                    onClick={() => setMapResultsExpanded(true)}
                  >
                    Show more (+{hiddenMapResultCount})
                  </button>
                ) : null}
                {hiddenMapResultCount > 0 && mapResultsExpanded ? (
                  <button
                    type="button"
                    className="badge badge-sm badge-ghost mt-2 cursor-pointer border border-base-300"
                    onClick={() => setMapResultsExpanded(false)}
                  >
                    Show less
                  </button>
                ) : null}
              </div>
            ) : null}
          </CollapsibleEntityCard>
        ) : null}

        {isEmptyChainStep && priorStepsWithResults.length > 0 ? (
          <section className="mb-3 space-y-2">
            <div className="rounded-lg border border-base-200 px-2.5 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                Prior results
              </p>
              <div className="mt-2 space-y-3">
                {priorStepsWithResults.map(({ step, idx }) => {
                  const points = step.result?.mapPoints ?? []
                  const stepExpanded = Boolean(priorResultsExpandedByStep[step.id])
                  const visiblePoints = stepExpanded
                    ? points
                    : points.slice(0, MAP_RESULTS_PREVIEW_COUNT)
                  const hiddenCount = Math.max(0, points.length - MAP_RESULTS_PREVIEW_COUNT)
                  return (
                    <div key={step.id}>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                        {operationStepLabel(step, idx)}
                      </p>
                      <ul className="space-y-1">
                        {visiblePoints.map((point) => {
                          const selected =
                            activeStep?.inputSource?.kind === 'priorPoint' &&
                            activeStep.inputSource.fromStepId === step.id &&
                            activeStep.inputSource.pointId === point.id
                          return (
                            <OutputResultPointRow
                              key={point.id}
                              point={point}
                              fromStepId={step.id}
                              selected={selected}
                              onSelect={() => onPickPriorPoint(step.id, point.id)}
                              mapRef={mapRef}
                            />
                          )
                        })}
                      </ul>
                      {hiddenCount > 0 && !stepExpanded ? (
                        <button
                          type="button"
                          className="badge badge-sm badge-ghost mt-2 cursor-pointer border border-base-300"
                          onClick={() =>
                            setPriorResultsExpandedByStep((prev) => ({
                              ...prev,
                              [step.id]: true,
                            }))
                          }
                        >
                          Show more (+{hiddenCount})
                        </button>
                      ) : null}
                      {hiddenCount > 0 && stepExpanded ? (
                        <button
                          type="button"
                          className="badge badge-sm badge-ghost mt-2 cursor-pointer border border-base-300"
                          onClick={() =>
                            setPriorResultsExpandedByStep((prev) => ({
                              ...prev,
                              [step.id]: false,
                            }))
                          }
                        >
                          Show less
                        </button>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          </section>
        ) : null}

        {myLocationPlace ? (
          <EntityPanelCard
            expanded={myLocationPlace.focused}
            onFocus={() => onFocusResultPoint(myLocationPlace.point.id)}
            onClear={() => onClearMyLocation?.()}
            clearLabel="Clear my location"
            overview={
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: myLocationPlace.point.color }}
                    aria-hidden
                  />
                  <h4 className="truncate text-sm font-semibold">
                    {myLocationPlace.point.name}
                  </h4>
                </div>
                <p className="mt-0.5 text-[11px] text-base-content/50">
                  {myLocationPlace.point.subtitle || 'Current GPS position'}
                </p>
              </div>
            }
            overviewDetails={
              <dl>
                <DetailRow
                  label="Coordinates"
                  value={`${myLocationPlace.point.lat.toFixed(4)}, ${myLocationPlace.point.lng.toFixed(4)}`}
                  valueClassName="font-mono"
                />
              </dl>
            }
            operations={
              myLocationPlace.operations ? (
                <PanelOperatorSection
                  key={`${myLocationPlace.operations.targetId}:${myLocationPlace.operations.openFocusToken}`}
                  variant={myLocationPlace.operations.variant}
                  operators={myLocationPlace.operations.operators}
                  operationRunning={myLocationPlace.operations.operationRunning}
                  destinationOptions={myLocationPlace.operations.destinationOptions}
                  preferredDestinationRef={myLocationPlace.operations.preferredDestinationRef}
                  operationResultOperatorId={
                    myLocationPlace.operations.operationResultOperatorId
                  }
                  openFocusToken={myLocationPlace.operations.openFocusToken}
                  onRun={myLocationPlace.operations.onRun}
                />
              ) : null
            }
            detailsExpanded={expandedEntityId === myLocationPlace.point.id}
            onToggleDetails={() => toggleEntityExpanded(myLocationPlace.point.id)}
          />
        ) : null}

        {focusedPlace ? (
          <EntityPanelCard
            expanded
            onFocus={() => onFocusResultPoint(focusedPlace.point.id)}
            onClear={() => onClearFocusedPlace?.()}
            clearLabel="Clear place focus"
            overview={
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: focusedPlace.point.color }}
                    aria-hidden
                  />
                  <h4 className="truncate text-sm font-semibold">{focusedPlace.point.name}</h4>
                </div>
                <p className="mt-0.5 text-[11px] text-base-content/50">
                  {focusedPlace.point.subtitle || 'Map place'}
                </p>
              </div>
            }
            overviewDetails={
              <dl>
                <DetailRow
                  label="Coordinates"
                  value={`${focusedPlace.point.lat.toFixed(4)}, ${focusedPlace.point.lng.toFixed(4)}`}
                  valueClassName="font-mono"
                />
              </dl>
            }
            operations={
              <PanelOperatorSection
                key={`${focusedPlace.operations.targetId}:${focusedPlace.operations.openFocusToken}`}
                variant={focusedPlace.operations.variant}
                operators={focusedPlace.operations.operators}
                operationRunning={focusedPlace.operations.operationRunning}
                destinationOptions={focusedPlace.operations.destinationOptions}
                preferredDestinationRef={focusedPlace.operations.preferredDestinationRef}
                operationResultOperatorId={focusedPlace.operations.operationResultOperatorId}
                openFocusToken={focusedPlace.operations.openFocusToken}
                onRun={focusedPlace.operations.onRun}
              />
            }
            detailsExpanded={expandedEntityId === focusedPlace.point.id}
            onToggleDetails={() => toggleEntityExpanded(focusedPlace.point.id)}
          />
        ) : null}

        {pinnedJobs.map((job) => {
          const focused = focusId === job.id
          return (
            <EntityPanelCard
              key={`job:${job.id}`}
              expanded={focused}
              onFocus={() => onFocusPinnedEntity(job.id, 'job')}
              onClear={() => onRemovePinnedEntity(job.id)}
              clearLabel="Unpin job"
              overview={
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: jobStatusColor(job.status) }}
                      aria-hidden
                    />
                    <h4 className="truncate text-sm font-semibold">{job.name}</h4>
                  </div>
                  <p className="mt-0.5 text-[11px] text-base-content/50">
                    Job{job.number ? ` · #${job.number}` : ''}
                  </p>
                  <span
                    className={`mt-1 badge badge-sm ${
                      statusTone(job.status) === 'error'
                        ? 'badge-error'
                        : statusTone(job.status) === 'warning'
                          ? 'badge-warning'
                          : statusTone(job.status) === 'success'
                            ? 'badge-success'
                            : 'badge-ghost'
                    }`}
                  >
                    {titleCase(job.status || 'unknown')}
                  </span>
                </div>
              }
              overviewDetails={
                <dl>
                  <DetailRow label="Status" value={titleCase(job.status || 'unknown')} />
                  <DetailRow label="Activity" value={job.active ? 'Active' : 'Inactive'} />
                  <DetailRow label="Address" value={job.address || job.locationName || '—'} />
                </dl>
              }
              operations={
                inlineEntityOperations?.targetId === job.id ? (
                  <PanelOperatorSection
                    key={`${job.id}:${inlineEntityOperations.openFocusToken}`}
                    variant={inlineEntityOperations.variant}
                    operators={inlineEntityOperations.operators}
                    operationRunning={inlineEntityOperations.operationRunning}
                    destinationOptions={inlineEntityOperations.destinationOptions}
                    preferredDestinationRef={inlineEntityOperations.preferredDestinationRef}
                    operationResultOperatorId={inlineEntityOperations.operationResultOperatorId}
                    openFocusToken={inlineEntityOperations.openFocusToken}
                    onRun={inlineEntityOperations.onRun}
                  />
                ) : null
              }
              detailsExpanded={expandedEntityId === job.id}
              onToggleDetails={() => toggleEntityExpanded(job.id)}
              details={
                <dl>
                  <DetailRow label="Number" value={job.number || '—'} />
                  <DetailRow label="Project type" value={job.projectType || '—'} />
                  <DetailRow label="State" value={job.state || '—'} />
                  <DetailRow label="Location" value={job.locationName || '—'} />
                  <DetailRow label="Account" value={job.accountName || '—'} />
                  <DetailRow
                    label="Schedule"
                    value={
                      job.scheduleStart || job.scheduleEnd
                        ? `${job.scheduleStart || '—'} → ${job.scheduleEnd || '—'}`
                        : '—'
                    }
                  />
                  <DetailRow
                    label="Coordinates"
                    value={`${job.lat.toFixed(4)}, ${job.lng.toFixed(4)}`}
                    valueClassName="font-mono"
                  />
                </dl>
              }
            />
          )
        })}

        {pinnedPeople.map((person) => {
          const focused = focusId === person.id
          return (
            <EntityPanelCard
              key={`person:${person.id}`}
              expanded={focused}
              onFocus={() => onFocusPinnedEntity(person.id, 'person')}
              onClear={() => onRemovePinnedEntity(person.id)}
              clearLabel="Unpin person"
              overview={
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: PERSON_KIND_COLOR[person.personKind] }}
                      aria-hidden
                    />
                    <h4 className="truncate text-sm font-semibold">{person.name}</h4>
                  </div>
                  <p className="mt-0.5 text-[11px] text-base-content/50">
                    Person{person.roleName ? ` · ${person.roleName}` : ''}
                  </p>
                  <span
                    className={`mt-1 badge badge-sm ${
                      person.personKind === 'employee' ? 'badge-success' : 'badge-ghost'
                    }`}
                  >
                    {personKindLabel(person.personKind)}
                  </span>
                </div>
              }
              overviewDetails={
                <dl>
                  <DetailRow
                    label="Clocked in"
                    value={
                      person.clockedIn
                        ? [
                            person.jobName || 'Yes',
                            person.jobNumber ? `#${person.jobNumber}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : 'No'
                    }
                  />
                  <DetailRow
                    label="Location"
                    value={person.locationName || person.address || '—'}
                  />
                </dl>
              }
              operations={
                inlineEntityOperations?.targetId === person.id ? (
                  <PanelOperatorSection
                    key={`${person.id}:${inlineEntityOperations.openFocusToken}`}
                    variant={inlineEntityOperations.variant}
                    operators={inlineEntityOperations.operators}
                    operationRunning={inlineEntityOperations.operationRunning}
                    destinationOptions={inlineEntityOperations.destinationOptions}
                    preferredDestinationRef={inlineEntityOperations.preferredDestinationRef}
                    operationResultOperatorId={inlineEntityOperations.operationResultOperatorId}
                    openFocusToken={inlineEntityOperations.openFocusToken}
                    onRun={inlineEntityOperations.onRun}
                  />
                ) : null
              }
              detailsExpanded={expandedEntityId === person.id}
              onToggleDetails={() => toggleEntityExpanded(person.id)}
              details={
                <dl>
                  <DetailRow label="Kind" value={personKindLabel(person.personKind)} />
                  <DetailRow label="Role" value={person.roleName || '—'} />
                  <DetailRow label="Email" value={person.email || '—'} />
                  <DetailRow label="Phone" value={person.phone || '—'} />
                  {person.portalName ? (
                    <DetailRow label="Portal contact" value={person.portalName} />
                  ) : null}
                  {person.clockedIn ? (
                    <DetailRow
                      label="Clocked since"
                      value={formatClockedSince(person.startedAt)}
                    />
                  ) : null}
                  <DetailRow label="Address" value={person.address || '—'} />
                  <DetailRow
                    label="Coordinates"
                    value={
                      personHasMapPin(person)
                        ? `${Number(person.lat).toFixed(4)}, ${Number(person.lng).toFixed(4)}`
                        : '—'
                    }
                    valueClassName="font-mono"
                  />
                </dl>
              }
            />
          )
        })}

        {pinnedAssets.map((asset) => {
          const focused = focusId === asset.id
          const assetIssues = assetIssuesById.get(asset.id) ?? []
          return (
            <EntityPanelCard
              key={`asset:${asset.id}`}
              expanded={focused}
              onFocus={() => onFocusPinnedEntity(asset.id, 'asset')}
              onClear={() => onRemovePinnedEntity(asset.id)}
              clearLabel="Unpin asset"
              overview={
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: ASSET_KIND_COLOR[asset.assetKind] }}
                      aria-hidden
                    />
                    <h4 className="truncate text-sm font-semibold">{asset.name}</h4>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] text-base-content/50">
                    <FontAwesomeIcon icon={faSatelliteDish} className="text-primary" />
                    {assetKindLabel(asset.assetKind)}
                  </p>
                  {assetIssues.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {assetIssues.map((issue) => (
                        <span
                          key={issue.code}
                          className="inline-flex items-center rounded-md bg-warning/20 px-1.5 py-0.5 text-[10px] font-semibold text-warning"
                          title={issue.detail || issue.label}
                        >
                          <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
                          {issue.label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span
                      className={`mt-1 badge badge-sm ${
                        statusTone(asset.status) === 'error'
                          ? 'badge-error'
                          : statusTone(asset.status) === 'warning'
                            ? 'badge-warning'
                            : statusTone(asset.status) === 'success'
                              ? 'badge-success'
                              : 'badge-ghost'
                      }`}
                    >
                      {asset.status}
                    </span>
                  )}
                </div>
              }
              overviewDetails={
                <dl>
                  <DetailRow
                    label="Status"
                    value={asset.status}
                    valueClassName={
                      assetIssues.some((x) => x.code === 'offline')
                        ? 'text-warning font-semibold'
                        : undefined
                    }
                  />
                  <DetailRow
                    label="Last GPS"
                    value={formatGpsMs(
                      getEntityLastGpsTimestampMs({
                        key: asset.id,
                        lat: asset.lat,
                        lng: asset.lng,
                        displayName: asset.name,
                        assetTracker: asset.assetKind === 'assetTracker',
                        fleetId: asset.fleetId,
                        raw: asset.raw,
                      }),
                    )}
                    valueClassName={
                      assetIssues.some((x) => x.code === 'staleCheckIn' || x.code === 'noRecentGps')
                        ? 'text-warning font-semibold'
                        : undefined
                    }
                  />
                  <DetailRow
                    label="Driver"
                    value={asset.driver || String(asset.raw.appDriverName || '') || '—'}
                  />
                </dl>
              }
              operations={
                inlineEntityOperations?.targetId === asset.id ? (
                  <PanelOperatorSection
                    key={`${asset.id}:${inlineEntityOperations.openFocusToken}`}
                    variant={inlineEntityOperations.variant}
                    operators={inlineEntityOperations.operators}
                    operationRunning={inlineEntityOperations.operationRunning}
                    destinationOptions={inlineEntityOperations.destinationOptions}
                    preferredDestinationRef={inlineEntityOperations.preferredDestinationRef}
                    operationResultOperatorId={inlineEntityOperations.operationResultOperatorId}
                    openFocusToken={inlineEntityOperations.openFocusToken}
                    onRun={inlineEntityOperations.onRun}
                  />
                ) : null
              }
              detailsExpanded={expandedEntityId === asset.id}
              onToggleDetails={() => toggleEntityExpanded(asset.id)}
              details={
                <>
                  <dl>
                    <DetailRow
                      label="Fleet (group)"
                      value={
                        asset.fleetId !== 0
                          ? fleetGroupLabels.has(asset.fleetId)
                            ? `${fleetGroupLabels.get(asset.fleetId)} (${asset.fleetId})`
                            : String(asset.fleetId)
                          : null
                      }
                    />
                    <DetailRow
                      label="Speed"
                      value={
                        typeof asset.raw.speed === 'number'
                          ? `${Math.round(asset.raw.speed)} mph`
                          : null
                      }
                    />
                    <DetailRow label="Heading" value={String(asset.raw.heading || '')} />
                    <DetailRow label="VIN" value={String(asset.raw.vin || '')} />
                    <DetailRow
                      label="Make / Model"
                      value={`${asset.raw.make != null ? String(asset.raw.make) : ''} ${
                        asset.raw.model != null ? String(asset.raw.model) : ''
                      }`.trim()}
                    />
                    <DetailRow label="Year" value={String(asset.raw.year || '')} />
                    <DetailRow label="Plate" value={String(asset.raw.licensePlate || '')} />
                    <DetailRow
                      label="Fuel"
                      value={asset.fuelLevel || String(asset.raw.fuelLevel || '')}
                    />
                    <DetailRow
                      label="Battery"
                      value={asset.battery || String(asset.raw.battery || '')}
                    />
                    {assetIssues.some((x) => x.code === 'lowBattery') ? (
                      <DetailRow
                        label="Battery warning"
                        value={
                          assetIssues.find((x) => x.code === 'lowBattery')?.detail || 'Low battery'
                        }
                        valueClassName="text-warning font-semibold"
                      />
                    ) : null}
                    {assetIssues.some((x) => x.code === 'staleCheckIn') ? (
                      <DetailRow
                        label="Check-in warning"
                        value={
                          assetIssues.find((x) => x.code === 'staleCheckIn')?.detail ||
                          'Stale check-in'
                        }
                        valueClassName="text-warning font-semibold"
                      />
                    ) : null}
                    <DetailRow
                      label="Odometer (mi)"
                      value={asset.raw.trueOdo != null ? String(asset.raw.trueOdo) : null}
                    />
                    <DetailRow
                      label="Est. miles (device)"
                      value={formatRoundedMiles(asset.raw.virtualOdo)}
                    />
                    <DetailRow
                      label="Engine hours"
                      value={
                        asset.raw.engineHours != null ? String(asset.raw.engineHours) : null
                      }
                    />
                    <DetailRow
                      label="Coordinates"
                      value={`${asset.lat.toFixed(4)}, ${asset.lng.toFixed(4)}`}
                      valueClassName="font-mono"
                    />
                  </dl>
                  {focused && focusedAssetImages.length > 0 ? (
                    <div className="mt-1 border-t border-base-200 pt-3">
                      <div className="mb-2 text-xs font-semibold text-base-content/70">
                        Asset Images
                      </div>
                      <div className="card flex h-48 w-full items-center justify-center bg-base-200 shadow">
                        <img
                          src={focusedAssetImages[assetGalleryIdx] || focusedAssetImages[0]}
                          alt="Asset"
                          className="h-full w-full rounded object-contain"
                        />
                      </div>
                      {focusedAssetImages.length > 1 ? (
                        <div className="mt-2 flex items-center justify-center gap-2">
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            onClick={() =>
                              onAssetGalleryIdxChange(
                                (idx) =>
                                  (idx - 1 + focusedAssetImages.length) %
                                  focusedAssetImages.length,
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <span className="text-[11px] text-base-content/70">
                            {assetGalleryIdx + 1} / {focusedAssetImages.length}
                          </span>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            onClick={() =>
                              onAssetGalleryIdxChange(
                                (idx) => (idx + 1) % focusedAssetImages.length,
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {focused && focusedAssetImages.length === 0 && assetImagesLoading ? (
                    <div className="pt-2 text-[11px] text-base-content/50">
                      Loading asset images…
                    </div>
                  ) : null}
                </>
              }
            />
          )
        })}

        {pinnedMunicipalities.map((muni) => {
          const focused = focusId === muni.id
          return (
            <EntityPanelCard
              key={`municipality:${muni.id}`}
              expanded={focused}
              onFocus={() => onFocusPinnedEntity(muni.id, 'municipality')}
              onClear={() => onRemovePinnedEntity(muni.id)}
              clearLabel="Unpin municipality"
              overview={
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: muni.isExternal
                          ? EXTERNAL_MUNICIPALITY_COLOR
                          : MUNICIPALITY_LEVEL_COLOR[muni.level],
                      }}
                      aria-hidden
                    />
                    <h4 className="truncate text-sm font-semibold">{muni.name}</h4>
                  </div>
                  <p className="mt-0.5 text-[11px] text-base-content/50">
                    {muni.isExternal
                      ? `Census ${titleCase(muni.level)} · Not in license database`
                      : `${licenseTypeLabel(muni.licenseType)} · ${titleCase(muni.level)}`}
                  </p>
                </div>
              }
              overviewDetails={
                <dl>
                  <DetailRow label="Level" value={titleCase(muni.level)} />
                  <DetailRow label="State" value={muni.state || 'N/A'} />
                  <DetailRow
                    label="License type"
                    value={muni.isExternal ? 'N/A' : licenseTypeLabel(muni.licenseType)}
                  />
                  {focused && (boundaryLoading || hasBoundary) ? (
                    <div className="mt-1 rounded-lg border border-base-200 bg-base-200/60 px-2 py-1.5 text-[11px]">
                      {boundaryLoading ? (
                        <span className="flex items-center gap-2 text-base-content/70">
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                          Loading Census boundary…
                        </span>
                      ) : (
                        <span className="text-base-content/70">
                          Boundary loaded · GEOID{' '}
                          <span className="font-mono">{muni.censusGeoid}</span>
                        </span>
                      )}
                    </div>
                  ) : null}
                </dl>
              }
              operations={
                inlineEntityOperations?.targetId === muni.id ? (
                  <PanelOperatorSection
                    key={`${muni.id}:${inlineEntityOperations.openFocusToken}`}
                    variant={inlineEntityOperations.variant}
                    operators={inlineEntityOperations.operators}
                    operationRunning={inlineEntityOperations.operationRunning}
                    destinationOptions={inlineEntityOperations.destinationOptions}
                    preferredDestinationRef={inlineEntityOperations.preferredDestinationRef}
                    operationResultOperatorId={inlineEntityOperations.operationResultOperatorId}
                    openFocusToken={inlineEntityOperations.openFocusToken}
                    onRun={inlineEntityOperations.onRun}
                  />
                ) : null
              }
              detailsExpanded={expandedEntityId === muni.id}
              onToggleDetails={() => toggleEntityExpanded(muni.id)}
              details={
                <>
                  <dl>
                    <DetailRow
                      label="License #"
                      value={muni.isExternal ? 'N/A' : muni.licenseNumber || 'N/A'}
                    />
                    <DetailRow
                      label="Expires"
                      value={muni.isExternal ? 'N/A' : muni.expirationDate || '—'}
                    />
                    <DetailRow
                      label="Date obtained"
                      value={muni.isExternal ? 'N/A' : muni.obtainedDate || '—'}
                    />
                    <DetailRow
                      label="Annual cost"
                      value={
                        muni.isExternal
                          ? 'N/A'
                          : muni.annualCost != null
                            ? `$${muni.annualCost.toLocaleString()}`
                            : '—'
                      }
                    />
                    <DetailRow
                      label="Qualifier"
                      value={muni.isExternal ? 'N/A' : muni.qualifier || '—'}
                    />
                    <DetailRow
                      label="License link"
                      value={
                        muni.isExternal ? (
                          'N/A'
                        ) : muni.licenseUrl ? (
                          <a
                            href={muni.licenseUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="link link-primary"
                          >
                            View on municipality site
                          </a>
                        ) : (
                          '—'
                        )
                      }
                    />
                    <DetailRow
                      label="Census GEOID"
                      value={muni.censusGeoid || '—'}
                      valueClassName="font-mono"
                    />
                    <DetailRow
                      label="Coordinates"
                      value={`${muni.lat.toFixed(4)}, ${muni.lng.toFixed(4)}`}
                      valueClassName="font-mono"
                    />
                  </dl>
                  {!muni.isExternal && (muni.licenseImageFront || muni.licenseImageBack) ? (
                    <div className="mt-1 space-y-2 border-t border-base-200 pt-3">
                      <div className="text-xs font-semibold text-base-content/70">
                        License images
                      </div>
                      {muni.licenseImageFront ? (
                        <div className="card flex h-40 w-full items-center justify-center bg-base-200 shadow">
                          <img
                            src={muni.licenseImageFront}
                            alt="License front"
                            className="h-full w-full rounded object-contain"
                          />
                        </div>
                      ) : null}
                      {muni.licenseImageBack ? (
                        <div className="card flex h-40 w-full items-center justify-center bg-base-200 shadow">
                          <img
                            src={muni.licenseImageBack}
                            alt="License back"
                            className="h-full w-full rounded object-contain"
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </>
              }
            />
          )
        })}
      </div>
    </div>
  )
}
